import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

const AREAS = [
  'Dubai Marina', 'Palm Jumeirah', 'Downtown Dubai', 'Business Bay',
  'JVC (Jumeirah Village Circle)', 'Dubai Hills Estate', 'MBR City',
  'Dubai Harbour', 'Dubai Studio City', 'Jumeirah', 'Al Barsha',
  'DIFC', 'Dubai Creek Harbour', 'Emaar Beachfront', 'Arjan',
]

const BEDROOM_OPTIONS = [
  { label: 'Studio', value: '0' },
  { label: '1 BR',   value: '1' },
  { label: '2 BR',   value: '2' },
  { label: '3 BR',   value: '3' },
  { label: '4 BR',   value: '4' },
  { label: '5+ BR',  value: '5' },
]

import { GLOBAL_CURRENCIES, useCurrencies } from '../context/CurrencyContext'

const LIFESTYLE_OPTIONS = [
  { id: 'waterfront', label: 'Waterfront', icon: '🌊' },
  { id: 'family',     label: 'Family',     icon: '👨‍👩‍👧‍👦' },
  { id: 'luxury',     label: 'Luxury',     icon: '💎' },
  { id: 'investment', label: 'Investment', icon: '📈' },
  { id: 'golf',       label: 'Golf Course', icon: '⛳' },
  { id: 'urban',      label: 'Urban',       icon: '🏙️' },
  { id: 'nature',     label: 'Nature',      icon: '🌳' },
]

const SORT_OPTIONS = [
  { value: 'default',          label: 'Recommended' },
  { value: 'price_asc',        label: 'Price: Low → High' },
  { value: 'price_desc',       label: 'Price: High → Low' },
  { value: 'handover_soon',    label: 'Handover: Soonest' },
  { value: 'recently_updated', label: 'Recently Updated' },
]

// AED absolute bounds (all internal prices are in AED)
const PRICE_MIN_ABS = 300_000
const PRICE_MAX_ABS = 50_000_000

// Convert AED value to display currency
function toDisplay(aedVal, currency) {
  const cur = GLOBAL_CURRENCIES.find(c => c.code === currency) || GLOBAL_CURRENCIES[0]
  return aedVal * cur.rate
}

// Format a display price (already converted)
function fmtDisplay(val, currency) {
  const cur = GLOBAL_CURRENCIES.find(c => c.code === currency) || GLOBAL_CURRENCIES[0]
  if (val >= 1_000_000) return `${cur.code} ${(val / 1_000_000).toFixed(val % 1_000_000 < 50_000 ? 0 : 1)}M`
  if (val >= 1_000)     return `${cur.code} ${(val / 1_000).toFixed(0)}K`
  return `${cur.code} ${Math.round(val).toLocaleString()}`
}

// ── Close on outside click ───────────────────────────────────────────────────
function useClickOutside(callback) {
  const ref = useRef()
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) callback()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [callback])
  return ref
}

// ── Generic Pill Dropdown wrapper (portal-based to escape overflow:hidden) ────
function PillDrop({ id, label, icon, active, badge, children, minWidth = 200 }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos]   = useState({ top: 0, left: 0 })
  const btnRef = useRef()
  const panelRef = useRef()
  const close = useCallback(() => setOpen(false), [])

  // Close on outside click and reposition on scroll
  useEffect(() => {
    if (!open) return
    
    function updatePos() {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect()
        setPos({ top: rect.bottom + 8, left: rect.left })
      }
    }
    updatePos()

    function handler(e) {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) close()
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, close])

  function handleToggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, left: rect.left })
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={btnRef}
        id={id}
        onClick={handleToggle}
        className={`ofb-pill${active ? ' ofb-pill--on' : ''}`}
      >
        {icon && <span className="ofb-pill-icon">{icon}</span>}
        <span className="ofb-pill-label">{label}</span>
        {badge != null && badge > 0 && (
          <span className="ofb-pill-badge">{badge}</span>
        )}
        <span className={`ofb-chevron${open ? ' up' : ''}`}>›</span>
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="ofb-panel"
          style={{ minWidth, position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
        >
          {children(close)}
        </div>,
        document.body
      )}
    </div>
  )
}

// ── Currency Selector (portal-based) ─────────────────────────────────────────
function CurrencyPill({ value, onChange }) {
  const { currencies } = useCurrencies()
  const [open, setOpen] = useState(false)
  const [pos, setPos]   = useState({ top: 0, right: 0 })
  const btnRef   = useRef()
  const panelRef = useRef()
  const close = useCallback(() => setOpen(false), [])
  const sel = currencies.find(c => c.code === value) || currencies[0]

  useEffect(() => {
    if (!open) return
    
    function updatePos() {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect()
        setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
      }
    }
    updatePos()

    function handler(e) {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) close()
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, close])

  function handleToggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={btnRef}
        id="currency-dropdown-toggle"
        onClick={handleToggle}
        className="ofb-pill ofb-pill--ghost"
        title="Display currency"
      >
        <span>{sel.flag}</span>
        <span style={{ fontWeight: 700 }}>{sel.code}</span>
        <span className={`ofb-chevron${open ? ' up' : ''}`}>›</span>
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="ofb-panel"
          style={{ minWidth: 210, position: 'fixed', top: pos.top, right: pos.right, left: 'auto', zIndex: 9999 }}
        >
          <div className="ofb-panel-header">Display Currency</div>
          {currencies.map(c => (
            <button
              key={c.code}
              id={`currency-opt-${c.code.toLowerCase()}`}
              className={`ofb-panel-item${value === c.code ? ' on' : ''}`}
              onClick={() => { onChange(c.code); setOpen(false) }}
            >
              <span style={{ fontSize: '17px' }}>{c.flag}</span>
              <span style={{ fontWeight: 700, minWidth: 36 }}>{c.code}</span>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{c.name}</span>
              {value === c.code && <span style={{ marginLeft: 'auto', color: GOLD, fontSize: 13, fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// ── Budget Slider (displays in chosen currency, stores in AED) ───────────────
function BudgetSlider({ minAed, maxAed, currency, onChange }) {
  const minDisp  = toDisplay(minAed,       currency)
  const maxDisp  = toDisplay(maxAed,       currency)
  const minLimit = toDisplay(PRICE_MIN_ABS, currency)
  const maxLimit = toDisplay(PRICE_MAX_ABS, currency)
  const step     = toDisplay(100_000,       currency)     // 100K AED step in display currency

  const minFrac = (minAed - PRICE_MIN_ABS) / (PRICE_MAX_ABS - PRICE_MIN_ABS)
  const maxFrac = (maxAed - PRICE_MIN_ABS) / (PRICE_MAX_ABS - PRICE_MIN_ABS)

  const { currencies } = useCurrencies()
  // Convert display slider value back to AED for storage
  const cur = currencies.find(c => c.code === currency) || currencies[0]
  const toAed = v => Math.round(v / cur.rate)

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Min / Max display */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="ofb-slider-label">MIN</div>
          <div className="ofb-slider-val">{fmtDisplay(minDisp, currency)}</div>
        </div>
        <div style={{ alignSelf: 'center', color: '#cbd5e1' }}>—</div>
        <div style={{ textAlign: 'center' }}>
          <div className="ofb-slider-label">MAX</div>
          <div className="ofb-slider-val">
            {maxAed >= PRICE_MAX_ABS ? 'No limit' : fmtDisplay(maxDisp, currency)}
          </div>
        </div>
      </div>

      {/* Dual range track */}
      <div style={{ position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 5, background: '#e2e8f0', borderRadius: 4 }} />
        <div style={{
          position: 'absolute', height: 5, borderRadius: 4,
          background: `linear-gradient(90deg, ${GOLD}, #e8c97a)`,
          left: `${minFrac * 100}%`, right: `${(1 - maxFrac) * 100}%`,
        }} />
        {/* Min thumb */}
        <input type="range"
          className="ofb-range-input"
          id="budget-slider-min"
          min={minLimit} max={maxLimit} step={step}
          value={minDisp}
          onChange={e => {
            const v = Number(e.target.value)
            const aed = toAed(v)
            if (aed < maxAed) onChange(aed, maxAed)
          }}
          style={{ position: 'absolute', width: '100%', opacity: 0, height: 28, zIndex: 4 }}
        />
        {/* Max thumb */}
        <input type="range"
          className="ofb-range-input"
          id="budget-slider-max"
          min={minLimit} max={maxLimit} step={step}
          value={maxDisp}
          onChange={e => {
            const v = Number(e.target.value)
            const aed = toAed(v)
            if (aed > minAed) onChange(minAed, aed)
          }}
          style={{ position: 'absolute', width: '100%', opacity: 0, height: 28, zIndex: 5 }}
        />
        {/* Thumb visuals */}
        {[minFrac, maxFrac].map((f, i) => (
          <div key={i} style={{
            position: 'absolute', left: `${f * 100}%`,
            width: 18, height: 18, borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
            border: '2.5px solid #fff', boxShadow: '0 3px 8px rgba(197,160,89,0.5)',
            transform: 'translateX(-50%)', zIndex: 1, pointerEvents: 'none',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span className="ofb-slider-edge">{fmtDisplay(minLimit, currency)}</span>
        <span className="ofb-slider-edge">{fmtDisplay(maxLimit, currency)}+</span>
      </div>
    </div>
  )
}

// ── Checkbox list used inside dropdowns ──────────────────────────────────────
function CheckList({ options, selected, onChange, searchable = false }) {
  const [q, setQ] = useState('')
  const list = searchable ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options
  const toggle = val => onChange(selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val])
  return (
    <>
      {searchable && options.length > 5 && (
        <div style={{ padding: '8px 14px 4px' }}>
          <input
            className="ofb-search-input"
            type="text" placeholder="Search…"
            value={q} onChange={e => setQ(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {list.map(opt => (
          <button
            key={opt}
            className={`ofb-panel-item${selected.includes(opt) ? ' on' : ''}`}
            onClick={() => toggle(opt)}
          >
            <span className={`ofb-chk${selected.includes(opt) ? ' checked' : ''}`}>
              {selected.includes(opt) ? '✓' : ''}
            </span>
            <span style={{ fontSize: 13 }}>{opt}</span>
          </button>
        ))}
        {list.length === 0 && (
          <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13, fontFamily: FONT }}>No results</div>
        )}
      </div>
    </>
  )
}

// ── Main Filter Bar ──────────────────────────────────────────────────────────
export default function OffplanFilterBar({ filters, onFilterChange, developerList = [], areaList = [], emirateList = [], resultCount, totalCount, loading }) {
  const [expanded, setExpanded] = useState(true)
  const {
    searchQuery, minPrice, maxPrice,
    bedrooms, areas, emirates, lifestyles,
    developer, currency, areaUnit, sortBy,
  } = filters

  const set = (key, val) => onFilterChange({ ...filters, [key]: val })

  const hasActive =
    searchQuery ||
    minPrice > PRICE_MIN_ABS || maxPrice < PRICE_MAX_ABS ||
    bedrooms.length > 0 || areas.length > 0 || emirates.length > 0 ||
    lifestyles.length > 0 || developer.length > 0

  const { currencies } = useCurrencies()
  const budgetActive  = minPrice > PRICE_MIN_ABS || maxPrice < PRICE_MAX_ABS
  const cur = currencies.find(c => c.code === currency) || currencies[0]

  // Budget label (displayed in chosen currency)
  const budgetLabel = budgetActive
    ? `${fmtDisplay(toDisplay(minPrice, currency), currency)} – ${maxPrice >= PRICE_MAX_ABS ? 'No limit' : fmtDisplay(toDisplay(maxPrice, currency), currency)}`
    : `Budget`

  // Active filter chips
  const chips = []
  if (searchQuery) chips.push({ label: `"${searchQuery}"`, clear: () => set('searchQuery', '') })
  if (budgetActive) chips.push({ label: `${cur.flag} ${budgetLabel}`, clear: () => onFilterChange({ ...filters, minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS }) })
  bedrooms.forEach(b => chips.push({ label: b === '0' ? 'Studio' : `${b} BR`, clear: () => set('bedrooms', bedrooms.filter(x => x !== b)) }))
  areas.forEach(a => chips.push({ label: a, clear: () => set('areas', areas.filter(x => x !== a)) }))
  emirates.forEach(e => chips.push({ label: e, clear: () => set('emirates', emirates.filter(x => x !== e)) }))
  ;(Array.isArray(developer) ? developer : developer ? [developer] : []).forEach(d =>
    chips.push({ label: `Dev: ${d}`, clear: () => set('developer', Array.isArray(developer) ? developer.filter(x => x !== d) : '') })
  )
  lifestyles.forEach(l => {
    const opt = LIFESTYLE_OPTIONS.find(o => o.id === l)
    if (opt) chips.push({ label: `${opt.icon} ${opt.label}`, clear: () => set('lifestyles', lifestyles.filter(x => x !== l)) })
  })

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

        /* ── Container ── */
        .ofb-bar {
          position: sticky; top: 110px; z-index: 1000;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(0,46,83,0.07);
          box-shadow: 0 4px 24px rgba(0,46,83,0.07);
          isolation: isolate;
        }
        /* Collapsible body */
        .ofb-collapsible {
          overflow: hidden;
          transition: max-height 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease;
        }
        .ofb-collapsible.open  { max-height: 300px; opacity: 1; }
        .ofb-collapsible.shut  { max-height: 0;     opacity: 0; pointer-events: none; }
        /* Toggle tab */
        .ofb-toggle-tab {
          display: flex; align-items: center; justify-content: center; gap: 5px;
          width: 100%; padding: 4px 0 5px;
          background: none; border: none; cursor: pointer;
          font-family: ${FONT}; font-size: 11px; font-weight: 600;
          color: #94a3b8; letter-spacing: 0.4px;
          border-top: 1px solid rgba(0,46,83,0.05);
          transition: color 0.18s, background 0.18s;
        }
        .ofb-toggle-tab:hover { color: ${NAVY}; background: rgba(0,46,83,0.03); }
        .ofb-toggle-arrow {
          display: inline-flex; transition: transform 0.28s ease;
          font-size: 13px; line-height: 1;
        }
        .ofb-toggle-arrow.down { transform: rotate(180deg); }
        .ofb-inner {
          max-width: 1200px; margin: 0 auto;
          padding: 14px 20px 10px; box-sizing: border-box;
        }
        .ofb-row {
          display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; overflow-x: auto;
          scrollbar-width: none;
        }
        .ofb-row::-webkit-scrollbar { display: none; }

        /* ── Search box ── */
        .ofb-search {
          display: flex; align-items: center; gap: 8px;
          background: #f8fafc; border: 1.5px solid rgba(0,46,83,0.09);
          border-radius: 10px; padding: 8px 13px;
          transition: all 0.2s; flex-shrink: 0;
        }
        .ofb-search:focus-within {
          border-color: ${GOLD}; background: #fff;
          box-shadow: 0 0 0 3px rgba(197,160,89,0.1);
        }
        .ofb-search input {
          border: none; outline: none; background: transparent;
          font-family: ${FONT}; font-size: 13px; color: #1e293b;
          width: 160px;
        }
        .ofb-search input::placeholder { color: #94a3b8; }

        /* ── Pill buttons ── */
        .ofb-pill {
          display: flex; align-items: center; gap: 6px;
          background: #f8fafc; border: 1.5px solid rgba(0,46,83,0.09);
          border-radius: 10px; padding: 8px 13px;
          font-family: ${FONT}; font-size: 13px; font-weight: 600;
          color: #334155; cursor: pointer; white-space: nowrap; flex-shrink: 0;
          transition: all 0.18s ease;
        }
        .ofb-pill:hover {
          background: #f1f5f9; border-color: rgba(0,46,83,0.18);
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(0,46,83,0.07);
        }
        .ofb-pill--on {
          background: rgba(197,160,89,0.10);
          border-color: ${GOLD}; color: #7c5a12;
        }
        .ofb-pill--on:hover { background: rgba(197,160,89,0.16); }
        .ofb-pill--ghost {
          background: transparent; border-color: transparent;
          color: #64748b; padding: 8px 10px;
        }
        .ofb-pill--ghost:hover { background: #f1f5f9; border-color: rgba(0,46,83,0.09); transform: none; box-shadow: none; }
        .ofb-pill-icon { font-size: 14px; }
        .ofb-pill-label { font-size: 13px; }
        .ofb-pill-badge {
          background: ${GOLD}; color: #fff;
          font-size: 10px; font-weight: 700;
          padding: 1px 6px; border-radius: 12px; min-width: 18px; text-align: center;
        }
        .ofb-chevron {
          font-size: 13px; color: #94a3b8; line-height: 1;
          transform: rotate(90deg); transition: transform 0.18s;
        }
        .ofb-chevron.up { transform: rotate(-90deg); }

        /* ── Divider ── */
        .ofb-div { width: 1px; height: 24px; background: rgba(0,46,83,0.08); flex-shrink: 0; }

        /* ── Dropdown panel ── */
        .ofb-panel {
          position: absolute; top: calc(100% + 8px); left: 0;
          background: #fff; border-radius: 14px;
          border: 1px solid rgba(0,46,83,0.08);
          box-shadow: 0 20px 50px rgba(0,46,83,0.13), 0 4px 12px rgba(0,0,0,0.05);
          z-index: 500; overflow: hidden;
          animation: ofb-in 0.16s ease;
        }
        @keyframes ofb-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ofb-panel-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px 8px;
          font-family: ${FONT}; font-size: 10px; font-weight: 700;
          color: #94a3b8; letter-spacing: 1.2px; text-transform: uppercase;
          border-bottom: 1px solid rgba(0,46,83,0.05);
        }
        .ofb-panel-clear {
          background: none; border: none; cursor: pointer;
          font-family: ${FONT}; font-size: 11px; font-weight: 600;
          color: #ef4444; padding: 2px 6px; border-radius: 4px;
          transition: background 0.15s;
        }
        .ofb-panel-clear:hover { background: #fef2f2; }
        .ofb-panel-item {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 9px 16px;
          background: transparent; border: none;
          font-family: ${FONT}; font-size: 13px; font-weight: 500;
          color: #374151; cursor: pointer; text-align: left;
          transition: background 0.12s;
        }
        .ofb-panel-item:hover { background: #f8fafc; }
        .ofb-panel-item.on { background: rgba(197,160,89,0.07); color: #7c5a12; }
        .ofb-panel-item.on:hover { background: rgba(197,160,89,0.13); }

        /* ── Checkbox ── */
        .ofb-chk {
          width: 16px; height: 16px; border-radius: 4px;
          border: 1.5px solid rgba(0,46,83,0.18);
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 800; flex-shrink: 0;
          transition: all 0.12s;
        }
        .ofb-chk.checked { background: ${GOLD}; border-color: ${GOLD}; color: #fff; }

        /* ── Search inside panel ── */
        .ofb-search-input {
          width: 100%; box-sizing: border-box;
          border: 1.5px solid rgba(0,46,83,0.1); border-radius: 8px;
          padding: 7px 10px; font-family: ${FONT}; font-size: 12px;
          color: #1e293b; outline: none; background: #f8fafc;
          transition: border-color 0.15s;
        }
        .ofb-search-input:focus { border-color: ${GOLD}; }

        /* Budget panel */
        .ofb-budget-panel { padding: 16px 18px; min-width: 280px; }
        .ofb-slider-label {
          font-family: ${FONT}; font-size: 9px; font-weight: 700;
          color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 3px;
        }
        .ofb-slider-val {
          font-family: ${FONT}; font-size: 15px; font-weight: 700; color: ${GOLD};
        }
        .ofb-slider-edge {
          font-family: ${FONT}; font-size: 11px; color: #b0b8c8;
        }

        /* Dual Range Slider Fixes */
        .ofb-range-input {
          pointer-events: none;
          -webkit-appearance: none;
        }
        .ofb-range-input::-webkit-slider-thumb {
          pointer-events: auto;
          cursor: pointer;
          -webkit-appearance: none;
          width: 28px;
          height: 28px;
        }
        .ofb-range-input::-moz-range-thumb {
          pointer-events: auto;
          cursor: pointer;
          width: 28px;
          height: 28px;
        }

        /* ── Area unit toggle ── */
        .ofb-unit {
          display: flex; border: 1.5px solid rgba(0,46,83,0.09);
          border-radius: 10px; overflow: hidden; background: #f8fafc; flex-shrink: 0;
        }
        .ofb-unit-btn {
          background: none; border: none; padding: 8px 12px;
          font-family: ${FONT}; font-size: 12px; font-weight: 600;
          cursor: pointer; transition: all 0.15s; color: #64748b;
        }
        .ofb-unit-btn.on { background: ${NAVY}; color: #fff; }

        /* ── Sort ── */
        .ofb-sort {
          border: 1.5px solid rgba(0,46,83,0.09); border-radius: 10px;
          padding: 8px 10px; font-family: ${FONT}; font-size: 13px; font-weight: 600;
          color: #334155; background: #f8fafc; cursor: pointer; outline: none;
          transition: all 0.18s; appearance: none; flex-shrink: 0;
          padding-right: 28px; min-width: 140px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center;
          background-color: #f8fafc;
        }
        .ofb-sort:hover { border-color: rgba(0,46,83,0.18); }

        /* ── Clear all ── */
        .ofb-clear {
          border: 1.5px solid rgba(239,68,68,0.25); border-radius: 10px;
          padding: 7px 12px; font-family: ${FONT}; font-size: 12px;
          font-weight: 700; color: #dc2626; background: #fef2f2;
          cursor: pointer; transition: all 0.18s; white-space: nowrap; flex-shrink: 0;
        }
        .ofb-clear:hover { background: #fee2e2; }

        /* ── Lifestyle row ── */
        .ofb-life-row {
          display: flex; gap: 6px; flex-wrap: wrap;
          margin-top: 8px; align-items: center;
          padding-top: 8px; border-top: 1px solid rgba(0,46,83,0.05);
        }
        .ofb-life-label {
          font-family: ${FONT}; font-size: 9px; font-weight: 700;
          color: #b0b8c8; letter-spacing: 1.2px; text-transform: uppercase; white-space: nowrap;
        }
        .ofb-life-pill {
          display: flex; align-items: center; gap: 5px;
          border: 1.5px solid rgba(0,46,83,0.09); border-radius: 50px;
          padding: 5px 13px; font-family: ${FONT}; font-size: 12px; font-weight: 600;
          cursor: pointer; transition: all 0.18s;
          background: #f8fafc; color: #334155; white-space: nowrap;
        }
        .ofb-life-pill:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,46,83,0.07); }
        .ofb-life-pill.on {
          background: rgba(0,46,83,0.06); border-color: ${NAVY}; color: ${NAVY};
        }

        /* ── Chips ── */
        .ofb-chips {
          display: flex; flex-wrap: wrap; gap: 6px;
          padding: 8px 20px; max-width: 1200px; margin: 0 auto; box-sizing: border-box;
        }
        .ofb-chip {
          display: flex; align-items: center; gap: 5px;
          background: rgba(197,160,89,0.10); border: 1px solid rgba(197,160,89,0.30);
          border-radius: 50px; padding: 4px 11px;
          font-family: ${FONT}; font-size: 12px; font-weight: 600; color: #7c5a12;
          animation: chip-in 0.14s ease;
        }
        @keyframes chip-in { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
        .ofb-chip-x {
          background: none; border: none; cursor: pointer;
          font-size: 14px; color: #92681f; padding: 0; line-height: 1;
          opacity: 0.6; transition: opacity 0.12s;
        }
        .ofb-chip-x:hover { opacity: 1; }

        /* ── Result count ── */
        .ofb-count {
          font-family: ${FONT}; font-size: 13px; color: #64748b;
          padding: 10px 20px 6px; max-width: 1200px; margin: 0 auto; box-sizing: border-box;
        }
        .ofb-count strong { color: ${NAVY}; }

        @media (max-width: 1100px) {
          .ofb-bar { top: 80px; }
        }
        @media (max-width: 768px) {
          .ofb-bar { top: 70px; }
          .ofb-inner { padding: 8px 12px 6px; }
          .ofb-row { gap: 5px; }
          .ofb-search input { width: 90px; }
          .ofb-search { padding: 7px 10px; }
          .ofb-pill { padding: 7px 10px; font-size: 12px; }
          .ofb-pill-label { font-size: 12px; }
          .ofb-sort { min-width: 110px; font-size: 12px; padding: 7px 24px 7px 8px; }
          .ofb-life-label { display: none; }
        }
        @media (max-width: 480px) {
          .ofb-search input { width: 70px; }
          .ofb-unit { display: none; }
          .ofb-div { display: none; }
        }
      `}</style>

      <div className="ofb-bar">
        <div className={`ofb-collapsible ${expanded ? 'open' : 'shut'}`}>
        <div className="ofb-inner">

          {/* ── Main filter row ── */}
          <div className="ofb-row">

            {/* Search */}
            <div className="ofb-search">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, opacity: 0.45 }}>
                <circle cx="8.5" cy="8.5" r="5.75" stroke="#334155" strokeWidth="1.8" />
                <path d="M13 13l3.5 3.5" stroke="#334155" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <input
                id="offplan-search"
                type="text"
                placeholder="Project or developer…"
                value={searchQuery}
                onChange={e => set('searchQuery', e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => set('searchQuery', '')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
              )}
            </div>

            <div className="ofb-div" />

            {/* Budget */}
            <PillDrop
              id="budget-popover-toggle"
              label={budgetLabel}
              icon="💰"
              active={budgetActive}
              minWidth={280}
            >
              {(close) => (
                <div className="ofb-budget-panel">
                  <div className="ofb-panel-header">
                    Budget Range
                    {budgetActive && (
                      <button className="ofb-panel-clear"
                        onClick={() => onFilterChange({ ...filters, minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS })}>
                        Reset
                      </button>
                    )}
                  </div>
                  <div style={{ padding: '12px 0 4px' }}>
                    <BudgetSlider
                      minAed={minPrice} maxAed={maxPrice}
                      currency={currency}
                      onChange={(mn, mx) => onFilterChange({ ...filters, minPrice: mn, maxPrice: mx })}
                    />
                  </div>
                  <button onClick={close} style={{
                    marginTop: 14, width: '100%',
                    background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
                    border: 'none', borderRadius: 9, padding: 10,
                    fontFamily: FONT, fontSize: 13, fontWeight: 700,
                    color: '#fff', cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(197,160,89,0.32)',
                  }}>Apply</button>
                </div>
              )}
            </PillDrop>

            {/* Bedrooms */}
            <PillDrop
              id="bedroom-dropdown-toggle"
              label={bedrooms.length === 0 ? 'Bedrooms' : bedrooms.length === 1
                ? (BEDROOM_OPTIONS.find(o => o.value === bedrooms[0])?.label || bedrooms[0])
                : `${bedrooms.length} types`}
              icon="🛏"
              active={bedrooms.length > 0}
              badge={bedrooms.length || null}
              minWidth={180}
            >
              {() => (
                <>
                  <div className="ofb-panel-header">
                    Bedrooms
                    {bedrooms.length > 0 && (
                      <button className="ofb-panel-clear" onClick={() => set('bedrooms', [])}>Clear</button>
                    )}
                  </div>
                  {BEDROOM_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      id={`bedroom-opt-${opt.value}`}
                      className={`ofb-panel-item${bedrooms.includes(opt.value) ? ' on' : ''}`}
                      onClick={() => {
                        const next = bedrooms.includes(opt.value)
                          ? bedrooms.filter(x => x !== opt.value)
                          : [...bedrooms, opt.value]
                        set('bedrooms', next)
                      }}
                    >
                      <span className={`ofb-chk${bedrooms.includes(opt.value) ? ' checked' : ''}`}>
                        {bedrooms.includes(opt.value) ? '✓' : ''}
                      </span>
                      {opt.label}
                    </button>
                  ))}
                </>
              )}
            </PillDrop>

            {/* Area */}
            <PillDrop
              id="filter-area"
              label={areas.length === 0 ? 'Area' : areas.length === 1 ? areas[0] : `${areas.length} areas`}
              icon="📍"
              active={areas.length > 0}
              badge={areas.length || null}
              minWidth={230}
            >
              {() => {
                const combinedAreas = [...new Set(areaList)].sort()
                const areaOpts = combinedAreas.length > 0 ? combinedAreas : AREAS
                return (
                  <>
                    <div className="ofb-panel-header">
                      Area
                      {areas.length > 0 && (
                        <button className="ofb-panel-clear" onClick={() => set('areas', [])}>Clear</button>
                      )}
                    </div>
                    <CheckList
                      options={areaOpts} selected={areas}
                      onChange={v => set('areas', v)} searchable
                    />
                  </>
                )
              }}
            </PillDrop>

            {/* Emirate */}
            <PillDrop
              id="filter-emirate"
              label={emirates.length === 0 ? 'Emirate' : emirates.length === 1 ? emirates[0] : `${emirates.length} emirates`}
              icon="🇦🇪"
              active={emirates.length > 0}
              badge={emirates.length || null}
              minWidth={230}
            >
              {() => {
                const EMIRATES_DEFAULT = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain']
                const combinedEmirates = [...new Set([...EMIRATES_DEFAULT, ...emirateList])].sort()
                const emirateOpts = combinedEmirates.length > 0 ? combinedEmirates : EMIRATES_DEFAULT
                return (
                  <>
                    <div className="ofb-panel-header">
                      Emirate
                      {emirates.length > 0 && (
                        <button className="ofb-panel-clear" onClick={() => set('emirates', [])}>Clear</button>
                      )}
                    </div>
                    <CheckList
                      options={emirateOpts} selected={emirates}
                      onChange={v => set('emirates', v)} searchable
                    />
                  </>
                )
              }}
            </PillDrop>

            {/* Developer */}
            <PillDrop
              id="filter-developer"
              label={!developer ? 'Developer' : typeof developer === 'string' && developer ? developer : 'Developer'}
              icon="🏢"
              active={!!developer}
              minWidth={230}
            >
              {() => {
                const combinedDevs = [...new Set(developerList)].sort()
                const devOpts = combinedDevs.length > 0 ? combinedDevs : ['Loading…']
                const devSel  = developer ? [developer] : []
                return (
                  <>
                    <div className="ofb-panel-header">
                      Developer
                      {developer && (
                        <button className="ofb-panel-clear" onClick={() => set('developer', '')}>Clear</button>
                      )}
                    </div>
                    <CheckList
                      options={devOpts} selected={devSel}
                      onChange={v => set('developer', v.length > 0 ? v[v.length - 1] : '')}
                      searchable
                    />
                  </>
                )
              }}
            </PillDrop>

            <div className="ofb-div" />

            {/* Currency — ghost pill */}
            <CurrencyPill value={currency} onChange={v => set('currency', v)} />

            <div className="ofb-div" />

            {/* Sort */}
            <select
              id="offplan-sort"
              className="ofb-sort"
              value={sortBy}
              onChange={e => set('sortBy', e.target.value)}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Clear all */}
            {hasActive && (
              <button id="offplan-clear-all" className="ofb-clear"
                onClick={() => onFilterChange({
                  searchQuery: '', minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS,
                  bedrooms: [], areas: [], emirates: [], lifestyles: [], developer: '',
                  currency: 'AED', areaUnit: 'sqft', sortBy: 'default',
                })}>
                ✕ Clear
              </button>
            )}
          </div>

          {/* ── Lifestyle row ── */}
          <div className="ofb-life-row">
            <span className="ofb-life-label">Lifestyle:</span>
            {LIFESTYLE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                id={`lifestyle-${opt.id}`}
                className={`ofb-life-pill${lifestyles.includes(opt.id) ? ' on' : ''}`}
                onClick={() => {
                  const next = lifestyles.includes(opt.id)
                    ? lifestyles.filter(l => l !== opt.id)
                    : [...lifestyles, opt.id]
                  set('lifestyles', next)
                }}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>

          {/* ── Active chips ── */}
          {chips.length > 0 && (
            <div className="ofb-chips">
              {chips.map((chip, i) => (
                <div key={i} className="ofb-chip">
                  {chip.label}
                  <button className="ofb-chip-x" onClick={chip.clear}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>{/* end collapsible */}
        {/* ── Collapse tab ── */}
        <button
          id="ofb-collapse-toggle"
          className="ofb-toggle-tab"
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? 'Hide filters' : 'Show filters'}
        >
          <span className={`ofb-toggle-arrow${expanded ? '' : ' down'}`}>⌃</span>
          <span>{expanded ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>

      {/* Result count (below sticky bar) */}
      {!loading && (
        <div className="ofb-count">
          {resultCount === totalCount
            ? <>Showing all <strong>{totalCount}</strong> projects</>
            : <>Showing <strong>{resultCount}</strong> of <strong>{totalCount}</strong> projects</>}
          {hasActive && ' matching your filters'}
        </div>
      )}
    </>
  )
}
